import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  Building2,
  User,
  Phone,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { register } = useAuth();
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'الاسم مطلوب';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'الاسم يجب أن يكون 3 أحرف على الأقل';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صالح';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'رقم الجوال مطلوب';
    } else if (!/^(05\d{8}|9665\d{8}|5\d{8})$/.test(formData.phone.replace(/[\s\-]/g, ''))) {
      newErrors.phone = 'رقم الجوال غير صالح';
    }

    if (!formData.password) {
      newErrors.password = 'كلمة المرور مطلوبة';
    } else if (formData.password.length < 6) {
      newErrors.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'تأكيد كلمة المرور مطلوب';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'كلمات المرور غير متطابقة';
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'يجب الموافقة على الشروط والأحكام';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });
      
      if (result.success) {
        toast.success('تم إنشاء الحساب بنجاح');
        navigate('/dashboard');
      } else {
        const errorMessage = result.error || 'حدث خطأ أثناء التسجيل';
        toast.error(errorMessage);
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.message || 'حدث خطأ في الخادم');
      setLoading(false);
    }
  };

  const features = [
    'إدارة العروض العقارية بسهولة',
    'شبكة واسعة من المسوقين',
    'نظام مطابقة ذكي',
    'تحليلات وإحصائيات متقدمة',
    'دعم فني على مدار الساعة'
  ];

  return (
    <div className="register-page">
      <div className="register-background">
        <div className="bg-pattern"></div>
        <div className="bg-overlay"></div>
      </div>
      
      <div className="register-content">
        <div className="register-left">
          <div className="brand-section">
            <div className="brand-logo">
              <Building2 size={48} />
            </div>
            <h1>{t('common.appFullName')}</h1>
            <p>انضم إلى أكبر شبكة مسوقين عقاريين في المملكة</p>
          </div>
          
          <div className="features-list">
            {features.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="feature-icon">
                  <CheckCircle size={20} />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <div className="stats-section">
            <div className="stat">
              <span className="stat-value">5000+</span>
              <span className="stat-label">مسوّق عقاري</span>
            </div>
            <div className="stat">
              <span className="stat-value">50,000+</span>
              <span className="stat-label">عرض عقاري</span>
            </div>
            <div className="stat">
              <span className="stat-value">98%</span>
              <span className="stat-label">نسبة الرضا</span>
            </div>
          </div>
        </div>
        
        <div className="register-right">
          <div className="register-card">
            <div className="register-header">
              <h2>{t('auth.registerTitle')}</h2>
              <p>أنشئ حسابك وابدأ رحلتك في التسويق العقاري</p>
            </div>

            <form onSubmit={handleSubmit} className="register-form">
              <div className="form-group">
                <label htmlFor="name">
                  <User size={18} />
                  الاسم الكامل
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? 'error' : ''}`}
                  placeholder="أدخل اسمك الكامل"
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">
                    <Mail size={18} />
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control ${errors.email ? 'error' : ''}`}
                    placeholder="example@email.com"
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">
                    <Phone size={18} />
                    رقم الجوال
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-control ${errors.phone ? 'error' : ''}`}
                    placeholder="05xxxxxxxx"
                    dir="ltr"
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">
                    <Lock size={18} />
                    كلمة المرور
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`form-control ${errors.password ? 'error' : ''}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex="-1"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && <span className="error-text">{errors.password}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    <Lock size={18} />
                    تأكيد كلمة المرور
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex="-1"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                </div>
              </div>

              <div className="form-group terms-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                  />
                  <span>
                    أوافق على <Link to="/terms">الشروط والأحكام</Link> و <Link to="/privacy">سياسة الخصوصية</Link>
                  </span>
                </label>
                {errors.agreeTerms && <span className="error-text">{errors.agreeTerms}</span>}
              </div>

              <button 
                type="submit" 
                className="btn-register"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    جاري إنشاء الحساب...
                  </>
                ) : (
                  <>
                    إنشاء حساب جديد
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              <div className="login-link">
                <span>لديك حساب بالفعل؟</span>
                <Link to="/login">تسجيل الدخول</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
